// ═══════════════════════════════════════════════════════════════════════════
//  API CLIENT
// ═══════════════════════════════════════════════════════════════════════════
// One place that knows how to talk to the API, so nothing else has to
// remember the base URL, the token, or the error shape.
//
// ── Why the token is in localStorage AND a cookie ──────────────────────────
// The API sets an httpOnly `jk_admin` cookie, which is strictly the safer of
// the two — but a cookie only arrives if the console and the API share a site.
// In development they are :5174 and :4000 (same site, works); in production
// they may well be two different domains, where the cookie is third-party and
// Safari drops it outright. So login also returns the raw token, it is kept
// here, and it is sent as a bearer.
//
// The trade is real and worth naming: a bearer in localStorage is readable by
// any script that gets injected into this page, which a cookie would not be.
// What makes it acceptable is that this app renders no user-authored HTML, has
// no third-party scripts, and the token buys 12 hours at most — and against
// the alternative, an admin who simply cannot sign in on a cross-site deploy.

const BASE = (import.meta.env.VITE_API_URL ?? "http://localhost:4000").replace(
  /\/+$/,
  "",
);

const TOKEN_KEY = "jkmaxx.admin.token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Private mode, or storage blocked. The cookie may still carry the session
    // on a same-site deploy, so this is not fatal.
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* see getToken */
  }
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Fires when any request comes back 401, so the shell can bounce to login. */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler = () => {};

export function setUnauthorizedHandler(fn: UnauthorizedHandler): void {
  onUnauthorized = fn;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  query?: Record<string, string | number | boolean | undefined>;
}

export function apiUrl(
  path: string,
  query?: RequestOptions["query"],
): string {
  const url = new URL(BASE + path);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === "") continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

export async function api<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = getToken();

  const res = await fetch(apiUrl(path, options.query), {
    method: options.method ?? "GET",
    // Sends the cookie when the two share a site. Harmless when they do not.
    credentials: "include",
    signal: options.signal,
    headers: {
      ...(options.body !== undefined
        ? { "Content-Type": "application/json" }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (res.status === 401) {
    setToken(null);
    onUnauthorized();
    throw new ApiError("Your session has ended. Sign in again.", 401);
  }

  if (!res.ok) {
    // The API's error shape is fixed at `{ error }` — see its
    // middleware/errorHandler.ts. Anything else is a proxy or a crash.
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.error === "string") message = body.error;
    } catch {
      /* non-JSON body — keep the status message */
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * A download that has to carry the Authorization header, which a plain
 * `<a href>` cannot do. The blob is fetched, handed to the browser as an
 * object URL, and revoked — the alternative would be putting the session token
 * in a query string, where it lands in every proxy and server log between here
 * and the API.
 */
export async function downloadFile(
  path: string,
  filename: string,
  query?: RequestOptions["query"],
): Promise<void> {
  const token = getToken();
  const res = await fetch(apiUrl(path, query), {
    credentials: "include",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    throw new ApiError(`Export failed (${res.status})`, res.status);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
